import { useEffect } from 'react';

import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { BIZY_SETTINGS_DEEP_LINK_TRIGGER, BIZY_SETTINGS_SECTIONS, useBizySidePanelStore, type BizySettingsSection } from '../store/use-bizy-side-panel-store';

export const BIZY_SETTINGS_DEEP_LINK_PARAM = 'bizy_settings';

export function parseBizySettingsSection(search: string): BizySettingsSection | undefined {
	const value = new URLSearchParams(search).get(BIZY_SETTINGS_DEEP_LINK_PARAM);
	return BIZY_SETTINGS_SECTIONS.find((section) => section === value);
}

/**
 * Deep link into Bizy settings: `?bizy_settings=<section>` (e.g.
 * `?bizy_settings=connections`) opens the Bizy side panel with the settings
 * modal at that section, then strips the param via history.replaceState so
 * refreshes and back/forward navigation don't re-trigger it.
 *
 * The Bonusly desktop app uses this from its Connections tab, opening
 * `https://bonus.ly/?bizy_settings=connections` in the external browser
 * (BIZY-213). Works on any route hosted by a layout that mounts the Bizy
 * side panel (user, admin, super-admin).
 */
export function useBizySettingsDeepLink() {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	useEffect(() => {
		if (!isBizyEnabledForUser) return;

		const section = parseBizySettingsSection(window.location.search);
		if (!section) return;

		// BIZY_SETTINGS_DEEP_LINK_TRIGGER makes later 'auto' closes (session
		// restore on Rails admin pages, the intro-pending close) no-op
		// while the settings modal is showing, so they can't undo the deep link.
		const store = useBizySidePanelStore.getState();
		store.open(BIZY_SETTINGS_DEEP_LINK_TRIGGER);
		store.showSettings(section);

		const url = new URL(window.location.href);
		url.searchParams.delete(BIZY_SETTINGS_DEEP_LINK_PARAM);
		window.history.replaceState(window.history.state, '', url);
	}, [isBizyEnabledForUser]);
}
